import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../lib/auth';

// ── Language switcher ──────────────────────────────────────────────────────────
const LANGS = [
  { code: 'en', flag: '🇬🇧', label: 'English' },
  { code: 'es', flag: '🇪🇸', label: 'Español' },
  { code: 'de', flag: '🇩🇪', label: 'Deutsch' },
] as const;

function LanguageSwitcher({ compact }: { compact?: boolean }) {
  const { i18n } = useTranslation();
  const current = LANGS.find(l => l.code === i18n.language) ?? LANGS[0];
  return (
    <select
      value={i18n.language}
      onChange={e => { i18n.changeLanguage(e.target.value); localStorage.setItem('mw_lang', e.target.value); }}
      style={{
        fontSize: compact ? 11 : 12,
        background: 'none',
        border: '1px solid #e5e5e5',
        borderRadius: 6,
        padding: compact ? '2px 4px' : '4px 8px',
        color: '#374151',
        cursor: 'pointer',
        appearance: 'none',
        WebkitAppearance: 'none',
      }}
      title="Language"
    >
      {LANGS.map(l => (
        <option key={l.code} value={l.code}>{l.flag} {compact ? '' : l.label}</option>
      ))}
    </select>
  );
}

const NAV_CUSTOMER = [
  { to: '/',                  label: 'Dashboard',            mobileLabel: 'Home',      icon: GridIcon       },
  { to: '/registrations',     label: 'Registered Equipment', mobileLabel: 'Equipment', icon: ShieldIcon     },
  { to: '/registrations/new', label: 'Register Equipment',   mobileLabel: 'Register',  icon: PlusCircleIcon },
  { to: '/claims',            label: 'Warranty Claims',      mobileLabel: 'Claims',    icon: WrenchIcon     },
  { to: '/settings',          label: 'Settings',             mobileLabel: 'Settings',  icon: UserCogIcon    },
];

// Support & Help only in sidebar (not bottom nav — 5 items max)
const NAV_CUSTOMER_SIDEBAR_EXTRA = [
  { to: '/support', label: 'Support & Help', icon: LifebuoyIcon },
];

const NAV_ADMIN = [
  { to: '/admin',                label: 'Overview',        mobileLabel: 'Home',     icon: GridIcon     },
  { to: '/admin/claims',         label: 'Claims',          mobileLabel: 'Claims',   icon: WrenchIcon   },
  { to: '/admin/registrations',  label: 'Registrations',   mobileLabel: 'Regs',     icon: ShieldIcon   },
  { to: '/admin/companies',      label: 'Companies',       mobileLabel: 'Cos',      icon: BuildingIcon },
  { to: '/admin/users',          label: 'Users',           mobileLabel: 'Users',    icon: UsersIcon    },
  { to: '/admin/products',       label: 'Products',        mobileLabel: 'Products', icon: BoxIcon      },
  { to: '/admin/distributors',   label: 'Distributors',    mobileLabel: 'Dist.',    icon: TruckIcon    },
];

export default function Layout({ children }: { children: React.ReactNode }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const nav = user?.role === 'admin' ? NAV_ADMIN : NAV_CUSTOMER;
  const sidebarExtra = user?.role === 'admin' ? [] : NAV_CUSTOMER_SIDEBAR_EXTRA;

  function handleLogout() { logout(); navigate('/login'); }

  const displayName = user?.firstName
    ? `${user.firstName}${user.lastName ? ' ' + user.lastName : ''}`.trim()
    : user?.email ?? '';

  const initials = user?.firstName
    ? (user.firstName[0] + (user.lastName?.[0] ?? '')).toUpperCase()
    : (user?.email?.[0] ?? '?').toUpperCase();

  function isActive(item: { to: string }) {
    return item.to === '/'
      ? location.pathname === '/'
      : location.pathname === item.to ||
        (location.pathname.startsWith(item.to + '/') &&
         !nav.some(other => other.to !== item.to && location.pathname.startsWith(other.to)));
  }

  return (
    <div style={{ background: '#f4f4f4', minHeight: '100vh' }}>

      {/* ── Mobile top bar ──────────────────────────────────── */}
      <header className="md:hidden flex items-center justify-between px-4 py-3 border-b"
        style={{ background: '#fff', borderColor: '#e5e5e5', position: 'sticky', top: 0, zIndex: 40 }}>
        <Link to="/" style={{ textDecoration: 'none' }}>
          <img
            src="https://cdn.shopify.com/s/files/1/0774/8055/8750/files/MontiPower_Same_Font_Copyright_Original_Black_Red_1200px.png?v=1787509899"
            alt="MONTIPOWER"
            style={{ height: 20, width: 'auto', objectFit: 'contain' }}
          />
        </Link>
        <div className="flex items-center gap-3">
          <LanguageSwitcher compact />
          <a
            href={`https://wa.me/15558854642?text=${encodeURIComponent(`Hi Montipower team,\n\nI'm ${displayName}.\n\n`)}`}
            target="_blank" rel="noopener noreferrer"
            style={{ color: '#25d366', display: 'flex', alignItems: 'center' }}
          >
            <WhatsAppIcon size={20} />
          </a>
          <button onClick={handleLogout}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center' }}
            title="Sign out">
            <div style={{ width: 30, height: 30, borderRadius: '50%', background: '#e00000', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700 }}>
              {initials}
            </div>
          </button>
        </div>
      </header>

      {/* ── Desktop layout ───────────────────────────────────── */}
      <div className="hidden md:flex" style={{ height: '100vh', overflow: 'hidden' }}>

        {/* Sidebar */}
        <aside className="w-56 flex-shrink-0 flex flex-col border-r" style={{ background: '#fff', borderColor: '#e5e5e5' }}>
          <div className="px-5 py-4 border-b" style={{ borderColor: '#e5e5e5' }}>
            <Link to="/" className="flex flex-col gap-1 select-none">
              <img
                src="https://cdn.shopify.com/s/files/1/0774/8055/8750/files/MontiPower_Same_Font_Copyright_Original_Black_Red_1200px.png?v=1787509899"
                alt="MONTIPOWER"
                style={{ height: 22, width: 'auto', objectFit: 'contain', objectPosition: 'left' }}
              />
              <span style={{ fontSize: 9, fontWeight: 600, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#aaa' }}>Warranty Portal</span>
            </Link>
          </div>
          <nav className="flex-1 px-2 py-3 overflow-y-auto space-y-0.5">
            {[...nav, ...sidebarExtra].map(item => {
              const active = isActive(item);
              const Icon = item.icon;
              return (
                <Link key={item.to} to={item.to}
                  className="flex items-center gap-2.5 px-3 py-2 rounded text-sm transition-colors duration-100"
                  style={{ fontWeight: active ? 600 : 400, background: active ? '#e00000' : 'transparent', color: active ? '#fff' : '#374151', textDecoration: 'none' }}
                  onMouseEnter={e => { if (!active) (e.currentTarget as HTMLElement).style.background = '#f4f4f4'; }}
                  onMouseLeave={e => { if (!active) (e.currentTarget as HTMLElement).style.background = 'transparent'; }}>
                  <Icon className="w-4 h-4 flex-shrink-0" style={{ opacity: active ? 1 : 0.5 }} />
                  {item.label}
                </Link>
              );
            })}
          </nav>
          <div className="border-t px-3 py-3" style={{ borderColor: '#e5e5e5' }}>
            <div className="flex items-center gap-2.5 mb-2">
              <div className="w-7 h-7 rounded-full flex items-center justify-center text-white flex-shrink-0"
                style={{ background: '#e00000', fontSize: 11, fontWeight: 700 }}>
                {initials}
              </div>
              <div className="min-w-0">
                <p className="text-xs font-semibold truncate" style={{ color: '#0b0d0e' }}>{displayName}</p>
                <p className="truncate" style={{ fontSize: 10, color: '#9ca3af' }}>{user?.email}</p>
              </div>
            </div>
            <a href={`https://wa.me/15558854642?text=${encodeURIComponent(`Hi Montipower team,\n\nI'm ${displayName}${user?.email ? ` (${user.email})` : ''}.\n\n`)}`}
              target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-1.5 mb-1.5 transition-colors"
              style={{ fontSize: 11, color: '#25d366', textDecoration: 'none' }}
              onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = '#128c3e'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = '#25d366'; }}>
              <WhatsAppIcon size={12} />
              Chat with HQ
            </a>
            <div className="flex items-center justify-between mt-1.5">
              <button onClick={handleLogout}
                className="flex items-center gap-1.5 transition-colors"
                style={{ fontSize: 11, color: '#9ca3af', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = '#374151'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = '#9ca3af'; }}>
                <LogOutIcon className="w-3 h-3" />
                Sign out
              </button>
              <LanguageSwitcher compact />
            </div>
          </div>
        </aside>

        {/* Desktop main */}
        <main className="flex-1 overflow-y-auto flex flex-col">
          <div className="flex-1 max-w-6xl mx-auto w-full px-8 py-8">
            {children}
          </div>
          <PortalFooter />
        </main>
      </div>

      {/* ── Mobile main content ──────────────────────────────── */}
      <main className="md:hidden" style={{ paddingBottom: 72 }}>
        <div className="px-4 py-5">
          {children}
        </div>
        <PortalFooter mobile />
      </main>

      {/* ── Mobile bottom tab bar ────────────────────────────── */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-40 flex border-t"
        style={{ background: '#fff', borderColor: '#e5e5e5', paddingBottom: 'env(safe-area-inset-bottom)' }}>
        {nav.slice(0, 5).map(item => {
          const active = isActive(item);
          const Icon = item.icon;
          return (
            <Link key={item.to} to={item.to}
              className="flex-1 flex flex-col items-center justify-center py-2 gap-0.5"
              style={{ textDecoration: 'none', color: active ? '#e00000' : '#8f9b9b', minWidth: 0 }}>
              <Icon className="w-5 h-5" />
              <span style={{ fontSize: 10, fontWeight: active ? 600 : 400, lineHeight: 1.2 }}>
                {item.mobileLabel}
              </span>
            </Link>
          );
        })}
      </nav>
    </div>
  );
}

// ── Footer ────────────────────────────────────────────────────────────────────

function PortalFooter({ mobile }: { mobile?: boolean }) {
  return (
    <footer style={{ borderTop: '1px solid #e5e5e5', background: '#f4f4f4' }}>
      <div className={`max-w-6xl mx-auto py-6 ${mobile ? 'px-4' : 'px-8'}`}>
        <p className="text-xs mb-1" style={{ color: '#0b0d0e' }}>
          <strong>We care · Closing Cycles</strong>{' '}
          <span style={{ color: '#374151' }}>We work with people who care. Grit-free, circular surface preparation. People, Planet & Power.{' '}</span>
          <a href="https://montipower.com/pages/sustainability" target="_blank" rel="noopener noreferrer" style={{ color: '#e00000' }}>Our sustainability →</a>
        </p>
        <p className="text-xs mb-2" style={{ color: '#8f9b9b' }}>
          © {new Date().getFullYear()} MONTI Surface Tech Group B.V. · VAT NL8670.51.735.B01 / MontiPower®. All rights reserved.
        </p>
        <div className={`flex items-center ${mobile ? 'flex-col gap-3 items-start' : 'justify-between flex-wrap gap-4'}`}>
          <div className="flex flex-wrap gap-3">
            {[['Terms','https://montipower.com/pages/terms'],['Privacy','https://montipower.com/pages/privacy'],
              ['Legal notice','https://montipower.com/pages/impressum'],['Cookies','https://montipower.com/pages/cookie-policy'],
              ['Careers','https://montipower.com/pages/careers'],['Sitemap','https://montipower.com/sitemap.xml'],
            ].map(([label, href]) => (
              <a key={label} href={href} target="_blank" rel="noopener noreferrer"
                style={{ fontSize: 11, color: '#8f9b9b', textDecoration: 'none' }}>{label}</a>
            ))}
          </div>
          <div className="flex items-center gap-3">
            {[
              { href: 'https://www.linkedin.com/company/montipower',  icon: <LinkedInIcon /> },
              { href: 'https://www.youtube.com/user/MontiGermany',    icon: <YouTubeIcon /> },
              { href: 'https://www.instagram.com/montiwerkzeuge/',    icon: <InstagramIcon /> },
              { href: 'https://www.facebook.com/montiwerkzeuge',      icon: <FacebookIcon /> },
            ].map(({ href, icon }) => (
              <a key={href} href={href} target="_blank" rel="noopener noreferrer"
                style={{ color: '#8f9b9b', display: 'flex' }}>{icon}</a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}

// ── Icons ─────────────────────────────────────────────────────────────────────

function GridIcon({ className, style }: { className?: string; style?: React.CSSProperties }) {
  return <svg className={className} style={style} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>;
}
function ShieldIcon({ className, style }: { className?: string; style?: React.CSSProperties }) {
  return <svg className={className} style={style} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}><path strokeLinecap="round" strokeLinejoin="round" d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>;
}
function PlusCircleIcon({ className, style }: { className?: string; style?: React.CSSProperties }) {
  return <svg className={className} style={style} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}><circle cx="12" cy="12" r="9"/><path strokeLinecap="round" d="M12 8v8M8 12h8"/></svg>;
}
function WrenchIcon({ className, style }: { className?: string; style?: React.CSSProperties }) {
  return <svg className={className} style={style} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}><path strokeLinecap="round" strokeLinejoin="round" d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z"/></svg>;
}
function BoxIcon({ className, style }: { className?: string; style?: React.CSSProperties }) {
  return <svg className={className} style={style} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}><path strokeLinecap="round" strokeLinejoin="round" d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>;
}
function BuildingIcon({ className, style }: { className?: string; style?: React.CSSProperties }) {
  return <svg className={className} style={style} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}><path strokeLinecap="round" strokeLinejoin="round" d="M3 21h18M3 7l9-4 9 4M4 7v14M20 7v14M9 21V11h6v10"/></svg>;
}
function UsersIcon({ className, style }: { className?: string; style?: React.CSSProperties }) {
  return <svg className={className} style={style} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}><path strokeLinecap="round" strokeLinejoin="round" d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path strokeLinecap="round" strokeLinejoin="round" d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/></svg>;
}
function LifebuoyIcon({ className, style }: { className?: string; style?: React.CSSProperties }) {
  return <svg className={className} style={style} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}><circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="3.5"/><path strokeLinecap="round" d="M14.83 9.17l2.83-2.83M9.17 14.83l-2.83 2.83M14.83 14.83l2.83 2.83M9.17 9.17l-2.83-2.83"/></svg>;
}
function UserCogIcon({ className, style }: { className?: string; style?: React.CSSProperties }) {
  return <svg className={className} style={style} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}><circle cx="10" cy="7" r="4"/><path strokeLinecap="round" strokeLinejoin="round" d="M2 21v-2a4 4 0 014-4h5"/><circle cx="19" cy="18" r="2"/><path strokeLinecap="round" strokeLinejoin="round" d="M19 14v.5M19 22v-.5M14.9 16.1l.35.35M22.75 19.65l-.35-.35M14.9 19.9l.35-.35M22.75 16.35l-.35.35"/></svg>;
}
function TruckIcon({ className, style }: { className?: string; style?: React.CSSProperties }) {
  return <svg className={className} style={style} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}><rect x="1" y="3" width="15" height="13" rx="1"/><path strokeLinecap="round" strokeLinejoin="round" d="M16 8h4l3 5v3h-7V8z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>;
}
function LogOutIcon({ className }: { className?: string }) {
  return <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}><path strokeLinecap="round" strokeLinejoin="round" d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9"/></svg>;
}
function WhatsAppIcon({ size = 12 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
      <path d="M12 0C5.373 0 0 5.373 0 12c0 2.126.555 4.122 1.526 5.853L.057 23.428a.75.75 0 00.921.912l5.682-1.49A11.946 11.946 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.9 0-3.7-.5-5.25-1.37l-.374-.218-3.88 1.018 1.033-3.772-.235-.386A9.952 9.952 0 012 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z"/>
    </svg>
  );
}
function LinkedInIcon() { return <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-2-2 2 2 0 00-2 2v7h-4v-7a6 6 0 016-6zM2 9h4v12H2z"/><circle cx="4" cy="4" r="2"/></svg>; }
function YouTubeIcon() { return <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M22.54 6.42a2.78 2.78 0 00-1.95-1.96C18.88 4 12 4 12 4s-6.88 0-8.59.46a2.78 2.78 0 00-1.95 1.96A29 29 0 001 12a29 29 0 00.46 5.58A2.78 2.78 0 003.41 19.6C5.12 20 12 20 12 20s6.88 0 8.59-.46a2.78 2.78 0 001.95-1.95A29 29 0 0023 12a29 29 0 00-.46-5.58zM9.75 15.02V8.98L15.5 12l-5.75 3.02z"/></svg>; }
function InstagramIcon() { return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><circle cx="12" cy="12" r="4"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg>; }
function FacebookIcon() { return <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z"/></svg>; }
